// ─────────────────────────────────────────────────────────────────────────────
// SignalDetector.js — My Home Buddha Assistant
// Reads user data already in context/localStorage and generates prioritized
// signals for the Assistant Panel. Client-side Phase 1 — no Lambda needed.
// Called on login, cached in sessionStorage, refreshed every 4 hours.
// ─────────────────────────────────────────────────────────────────────────────

const CACHE_KEY  = "mhb_assistant_signals";
const CACHE_TTL  = 4 * 60 * 60 * 1000; // 4 hours

// ── Priority ordering ─────────────────────────────────────────────────────────
export const PRIORITY = { urgent: 0, high: 1, medium: 2, low: 3, info: 4 };

// ── Signal type → display config ──────────────────────────────────────────────
export const SIGNAL_CONFIG = {
  // Maintenance
  hvac_overdue:         { icon: "❄️",  color: "#E53E3E", audience: ["HO","B","S"] },
  roof_assess:          { icon: "🏠",  color: "#E53E3E", audience: ["HO","B","S"] },
  roof_replace:         { icon: "🏠",  color: "#C53030", audience: ["HO","B","S"] },
  water_heater:         { icon: "💧",  color: "#D97706", audience: ["HO","B","S"] },
  septic_pump:          { icon: "🔧",  color: "#D97706", audience: ["HO","B","S"] },
  water_test:           { icon: "🧪",  color: "#2D6A4F", audience: ["HO","B","S"] },
  seasonal_prep:        { icon: "🍂",  color: "#2D6A4F", audience: ["HO","B","S"] },
  reminder_due:         { icon: "🔔",  color: "#1A2280", audience: ["HO","B","S","AG","L"] },
  // Value Tracker
  equity_milestone:     { icon: "🏆",  color: "#D4921A", audience: ["HO","S"] },
  pmi_eligible:         { icon: "🎉",  color: "#2D6A4F", audience: ["HO"] },
  heloc_opportunity:    { icon: "💳",  color: "#319795", audience: ["HO"] },
  value_updated:        { icon: "📈",  color: "#1A2280", audience: ["HO","S"] },
  refinance_alert:      { icon: "💰",  color: "#D4921A", audience: ["HO"] },
  // Buyer signals
  new_matches:          { icon: "🏡",  color: "#1A2280", audience: ["B"] },
  profile_incomplete:   { icon: "📋",  color: "#D97706", audience: ["B","AG","L","HO","S"] },
  buyer_stale:          { icon: "⏰",  color: "#D97706", audience: ["B"] },
  preapproval_expiring: { icon: "📄",  color: "#E53E3E", audience: ["B"] },
  // Agent signals
  client_inactive:      { icon: "👤",  color: "#D97706", audience: ["AG","L"] },
  client_milestone:     { icon: "🎂",  color: "#D4921A", audience: ["AG","L"] },
  client_equity:        { icon: "💡",  color: "#2D6A4F", audience: ["AG","L"] },
  new_referral:         { icon: "🤝",  color: "#2D6A4F", audience: ["AG","L"] },
  buyer_active:         { icon: "🔥",  color: "#E53E3E", audience: ["AG","L"] },
  // General
  welcome_back:         { icon: "👋",  color: "#1A2280", audience: ["B","S","HO","AG","L","V"] },
  health_score:         { icon: "💯",  color: "#2D6A4F", audience: ["HO"] },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function daysSince(dateStr) {
  if (!dateStr) return null;
  return Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
}
function daysUntil(dateStr) {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr).getTime() - Date.now()) / 86400000);
}
function currentMonth() { return new Date().getMonth(); } // 0-indexed
function currentYear()  { return new Date().getFullYear(); }

// ── Main detector ─────────────────────────────────────────────────────────────
export function detectSignals(userDetails, mybuddhaRecords = [], options = {}) {
  const signals  = [];
  const profile  = userDetails?.userProfile || "B";
  const now      = new Date().toISOString();
  const firstName = userDetails?.firstName || "there";

  // Helper to add signal
  const add = (id, type, priority, title, body, cta = null, triggerData = {}, dismissible = true) => {
    signals.push({ id, type, priority, title, body, cta, triggerData,
      dismissible, dismissed: false, createdAt: now, audience: profile });
  };

  // ── GENERAL: Profile completion ───────────────────────────────────────────
  if (!userDetails?.profile_completed && ["AG","L","V"].includes(profile)) {
    add("profile_incomplete", "profile_incomplete", "high",
      "Complete Your Profile",
      "Your profile is incomplete. A complete profile helps clients find you and builds trust.",
      { label: "Complete Profile", url: "/myProfile" });
  }
  if (!userDetails?.buyerProfileCompleted && ["B","S","HO"].includes(profile)) {
    add("buyer_profile_incomplete", "profile_incomplete", "high",
      "Build Your Buyer Profile",
      "Answer a few questions about what you're looking for and unlock AI match scores on every listing.",
      { label: "Build My Profile", action: "openBuyerProfile" });
  }

  // ── MAINTENANCE signals (from HomeBuddha homeProfile records) ────────────
  const homeProfiles = mybuddhaRecords.filter(r => r.recordType === "homeProfile");
  const latestProfile = homeProfiles.sort((a,b) => (b.updatedAt||0) - (a.updatedAt||0))[0];

  if (latestProfile && ["HO","S","B"].includes(profile)) {
    const { systemAges = {}, qaAnswers = {}, categoryData = {} } = latestProfile;
    const year = currentYear();

    // HVAC
    if (systemAges.hvac?.installed) {
      const age = year - systemAges.hvac.installed;
      if (age >= 18) {
        add("hvac_replace", "hvac_overdue", "urgent",
          `HVAC System ${age} Years Old`,
          `Your HVAC is ${age} years old — well past its typical 15-20 year lifespan. Failure risk is high. Budget for replacement now.`,
          { label: "Find HVAC Pros", url: "https://www.myhomebuddha.com/find-pros" },
          { age, system: "hvac" });
      } else if (age >= 15) {
        add("hvac_aging", "hvac_overdue", "high",
          `HVAC Approaching End of Life (${age} yrs)`,
          `Your HVAC system is ${age} years old. Start planning for replacement — average cost is $5,000-$12,000.`,
          { label: "Get a Quote", url: "https://www.myhomebuddha.com/find-pros" },
          { age });
      }
    }
    // Annual HVAC service check
    if (systemAges.hvac?.lastServiced) {
      const daysSinceService = daysSince(systemAges.hvac.lastServiced + "-01-01");
      if (daysSinceService > 365) {
        add("hvac_service", "hvac_overdue", "medium",
          "HVAC Annual Service Due",
          "Your HVAC hasn't been serviced this year. Annual service extends life and maintains efficiency.",
          { label: "Schedule Service", url: "https://www.myhomebuddha.com/find-pros" },
          { daysSince: daysSinceService });
      }
    }
    // Roof
    if (systemAges.roof?.installed) {
      const age = year - systemAges.roof.installed;
      if (age >= 21) {
        add("roof_replace", "roof_replace", "urgent",
          `Roof ${age} Years Old — Replacement Window`,
          `Your roof is ${age} years old. Most roofs last 20-25 years. Get an inspection before you have a problem.`,
          { label: "Book Home Health Check-Up", url: "https://buy.stripe.com/8x26oGdxYa1zcc54ta8og1J" },
          { age });
      } else if (age >= 18) {
        add("roof_assess", "roof_assess", "high",
          `Roof Assessment Recommended (${age} yrs)`,
          `Your roof is ${age} years old. Now is the right time for a professional assessment before issues develop.`,
          { label: "Book Assessment", url: "https://buy.stripe.com/8x26oGdxYa1zcc54ta8og1J" },
          { age });
      }
    }
    // Water heater
    if (systemAges.waterHeater?.installed) {
      const age = year - systemAges.waterHeater.installed;
      if (age >= 10) {
        add("water_heater", "water_heater", age >= 12 ? "high" : "medium",
          `Water Heater ${age >= 12 ? "Overdue" : "Approaching"} Replacement`,
          `Your water heater is ${age} years old. Average lifespan is 10-12 years. ${age >= 12 ? "Failure risk is rising." : "Start budgeting for replacement."}`,
          { label: "Book Home Health Check-Up", url: "https://buy.stripe.com/8x26oGdxYa1zcc54ta8og1J" },
          { age });
      }
    }
    // Septic
    if (systemAges.septic?.lastPumped) {
      const yearsSince = year - systemAges.septic.lastPumped;
      if (yearsSince >= 3) {
        add("septic_pump", "septic_pump", "high",
          `Septic Pumping Overdue (${yearsSince} yrs)`,
          `Septic systems should be pumped every 3-5 years. Yours hasn't been pumped in ${yearsSince} years.`,
          { label: "Find Septic Pros", url: "https://www.myhomebuddha.com/find-pros" },
          { yearsSince });
      }
    }
    // Seasonal
    const month = currentMonth();
    if (month === 8) { // September — fall prep
      add("fall_prep", "seasonal_prep", "medium",
        "🍂 Fall Home Prep Time",
        "September is the ideal time to: check weatherstripping, service your heating system, clean gutters, and inspect the roof before winter.",
        { label: "See Fall Checklist", action: "openChecklist" });
    }
    if (month === 2) { // March — spring check
      add("spring_check", "seasonal_prep", "low",
        "🌱 Spring Home Check",
        "Spring is the right time to: check for winter damage, service your A/C, clean gutters, and inspect the exterior.",
        { label: "See Spring Checklist", action: "openChecklist" });
    }

    // Health score
    if (categoryData && Object.keys(categoryData).length > 0) {
      const scores = Object.values(categoryData).map(c => c.score || 0).filter(s => s > 0);
      if (scores.length > 0) {
        const avgScore = Math.round(scores.reduce((a, b) => a + b, 0) / scores.length);
        if (avgScore < 60) {
          add("health_score_low", "health_score", "high",
            `Home Health Score: ${avgScore}/100`,
            "Your home health score is below 60. There are items that need attention that could affect both safety and value.",
            { label: "View Home Health", action: "openHomeHealth" },
            { score: avgScore });
        } else if (avgScore >= 80) {
          add("health_score_good", "health_score", "info",
            `Home Health Score: ${avgScore}/100 ✅`,
            "Your home is in great shape! Keep up the maintenance to protect your investment.",
            null, { score: avgScore });
        }
      }
    }
  }

  // ── REMINDERS from mybuddha ───────────────────────────────────────────────
  const reminders = mybuddhaRecords.filter(r => r.recordType === "reminder" && r.status === "pending");
  const dueReminders = reminders.filter(r => {
    const daysUntilDue = daysUntil(new Date(r.scheduledFor).toISOString().split("T")[0]);
    return daysUntilDue !== null && daysUntilDue <= 7 && daysUntilDue >= 0;
  });
  dueReminders.slice(0, 3).forEach(r => {
    add(`reminder_${r.id}`, "reminder_due",
      r.priority === "urgent" ? "urgent" : r.priority === "high" ? "high" : "medium",
      r.title,
      r.message,
      r.ctaUrl ? { label: r.ctaLabel || "Take Action", url: r.ctaUrl } : null,
      { reminderId: r.id });
  });

  // ── VALUE TRACKER signals ─────────────────────────────────────────────────
  const tracker = mybuddhaRecords.find(r => r.recordType === "valueTracker");
  if (tracker && ["HO","S"].includes(profile)) {
    const equity    = tracker.currentEquity || 0;
    const avm       = tracker.currentAVM    || 0;
    const equityPct = avm > 0 ? equity / avm : 0;
    const apprecVal = avm - (tracker.purchasePrice || 0);

    if (equityPct >= 0.20) {
      add("pmi_eligible", "pmi_eligible", "high",
        "You May Be Able to Remove PMI 🎉",
        `You've reached ${(equityPct*100).toFixed(1)}% equity. Contact your lender to request PMI removal — this could save you $100-300/month.`,
        { label: "Learn How", url: "https://www.myhomebuddha.com/pmi" });
    }
    if (equityPct >= 0.25) {
      add("heloc_eligible", "heloc_opportunity", "medium",
        "HELOC Opportunity Available",
        `With ${(equityPct*100).toFixed(1)}% equity you may qualify for a Home Equity Line of Credit. Great for renovations or debt consolidation.`,
        { label: "Explore Options", action: "openValueTracker" });
    }
    // Value refresh if stale
    const lastRefresh = daysSince(tracker.lastAVMRefresh);
    if (lastRefresh !== null && lastRefresh > 30) {
      add("value_stale", "value_updated", "low",
        "Your Home Value Estimate is Stale",
        `Your last value refresh was ${lastRefresh} days ago. Refresh for current market data.`,
        { label: "Refresh Now", action: "openValueTracker" });
    }
    // Big appreciation milestone
    if (apprecVal >= 50000) {
      const rounded = Math.floor(apprecVal / 10000) * 10000;
      add(`equity_milestone_${rounded}`, "equity_milestone", "info",
        `Your Home Has Appreciated $${rounded.toLocaleString()}`,
        `Since purchase your home has gained an estimated $${apprecVal.toLocaleString()}. That's real wealth building.`,
        { label: "View Value Tracker", action: "openValueTracker" });
    }
  }

  // ── BUYER signals ─────────────────────────────────────────────────────────
  if (profile === "B") {
    const bp = userDetails?.buyerProfile;
    // Timeline check
    if (bp?.timeline === "Ready to move ASAP" || bp?.timeline === "Within 1-3 months") {
      add("buyer_urgent_timeline", "buyer_stale", "high",
        "Your Timeline is Coming Up 🏃",
        `You said you want to move ${bp.timeline.toLowerCase()}. Let's make sure you're on track — have you found the right home yet?`,
        { label: "Search Homes", url: "/homes" });
    }
    // Pre-approval
    if (bp?.financing === "Getting pre-approved now") {
      add("preapproval_nudge", "preapproval_expiring", "medium",
        "Getting Pre-Approved?",
        "Pre-approval takes about 15 minutes and dramatically increases your offer acceptance rate. Don't lose a home over this.",
        { label: "Find a Lender", url: "/lenders" });
    }
  }

  // ── AGENT signals (reads their network from invitationHistory + context) ───
  if (["AG","L"].includes(profile)) {
    const network = userDetails?.invitationHistory || [];
    const signedUp = network.filter(n => n.signedUp);
    const recentReferrals = signedUp.filter(n => {
      const days = daysSince(n.signedUpAt || n.sentAt);
      return days !== null && days <= 7;
    });
    if (recentReferrals.length > 0) {
      add("new_referrals", "new_referral", "high",
        `${recentReferrals.length} New Network Member${recentReferrals.length > 1 ? "s" : ""}! 🤝`,
        `${recentReferrals.map(r => r.firstName || r.email).join(", ")} just joined your network. Welcome them and start building the relationship.`,
        { label: "View My Network", url: "/myNetwork" });
    }
    // Profile CTA for agents
    if (!userDetails?.newdata?.companyName) {
      add("agent_profile_missing", "profile_incomplete", "medium",
        "Add Your Company & Branding",
        "Your profile is missing company info. Clients want to know who they're working with.",
        { label: "Complete Profile", url: "/myProfile" });
    }
  }

  // ── WELCOME BACK (always last, lowest priority) ───────────────────────────
  const lastLogin = daysSince(userDetails?.lastLogin || userDetails?.createdAt);
  if (lastLogin !== null && lastLogin > 14) {
    add("welcome_back", "welcome_back", "info",
      `Welcome Back, ${firstName}! 👋`,
      `It's been ${lastLogin} days since your last visit. Let's see what's changed with your home and network.`,
      null, { daysSince: lastLogin }, false);
  }

  // ── Sort by priority ──────────────────────────────────────────────────────
  signals.sort((a, b) => (PRIORITY[a.priority] || 99) - (PRIORITY[b.priority] || 99));

  return signals;
}

// ── Cache helpers ─────────────────────────────────────────────────────────────
export function getCachedSignals(userId) {
  try {
    const raw = sessionStorage.getItem(`${CACHE_KEY}_${userId}`);
    if (!raw) return null;
    const { signals, timestamp } = JSON.parse(raw);
    if (Date.now() - timestamp > CACHE_TTL) return null;
    return signals;
  } catch(e) { return null; }
}

export function setCachedSignals(userId, signals) {
  try {
    sessionStorage.setItem(`${CACHE_KEY}_${userId}`, JSON.stringify({ signals, timestamp: Date.now() }));
  } catch(e) {}
}

export function clearSignalCache(userId) {
  try { sessionStorage.removeItem(`${CACHE_KEY}_${userId}`); } catch(e) {}
}

export function getSignalCount(signals) {
  return signals.filter(s => !s.dismissed && s.priority !== "info").length;
}

export function getPrioritySignals(signals, max = 5) {
  return signals.filter(s => !s.dismissed).slice(0, max);
}